import { JSX } from "react";

import { Box, Paper, Theme, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Difference from "src/components/Difference/Difference";
import DifferenceChip from "src/components/Difference/DifferenceChip";
import sharedStyles from "src/constants/styles";
import { formatNum } from "src/lib/number.helpers";
import { type MeasuresSummarizedItemDto } from "src/types/dashboardDatasetDto";
import LocationType from "src/types/LocationType";

import PinnedItemMenu from "./PinnedItemMenu";

const getStyles = (theme: Theme) => ({
  hoverContent: {
    opacity: 0,
    pointerEvents: "none",
    transition: "opacity 0.3s",
  },
  cardContainer: {
    height: "100%",
    py: 2,
    px: 2.5,
    "&:hover": {
      borderColor: theme.palette.border.strong,
      "& .hoverContent": {
        opacity: 1,
        pointerEvents: "auto",
      },
    },
  },
  contentContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    height: "100%",
    "&:hover": { cursor: "pointer" },
  },
  spaceBetweenFlexbox: {
    ...sharedStyles(theme).spaceBetweenFlexbox,
    alignItems: "center",
  },
});

function PinnedItemComponent({ data }: { data: MeasuresSummarizedItemDto }): JSX.Element {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const currentValue = data.currentValue ?? 0;
  const pastValue = data.pastValue ?? 0;
  const diff = currentValue === 0 || pastValue === 0 ? 0 : ((currentValue - pastValue) / pastValue) * 100;

  return (
    <Paper
      sx={getStyles(theme).cardContainer}
      onClick={() => {
        if (data.type === LocationType.Shop) {
          navigate(`/shop-dashboard/${data.locationId}`);
        }
        if (data.type === LocationType.Mall) {
          navigate(`/center-dashboard/${data.locationId}`);
        }
        if (data.type === LocationType.Station) {
          navigate(`/station-dashboard/${data.locationId}`);
        }
      }}
    >
      <Box sx={getStyles(theme).contentContainer}>
        <Box>
          <Box sx={getStyles(theme).spaceBetweenFlexbox}>
            <Typography fontSize={theme.fontSizes.lg} fontWeight={theme.typography.fontWeightMedium} noWrap>
              {data.localName}
            </Typography>
            <Box className='hoverContent' sx={getStyles(theme).hoverContent}>
              <PinnedItemMenu locationId={data.locationId} type={data.type} />
            </Box>
          </Box>

          <Box mt={1} display='flex' flexWrap='wrap' alignItems='center'>
            <Typography fontSize={theme.fontSizes.lg} fontWeight={theme.typography.fontWeightMedium}>
              {formatNum(currentValue)}
            </Typography>
            <DifferenceChip difference={diff} fontSize={theme.fontSizes.xs} hasWhiteBackground />
            <Difference row={data} fontSize={theme.fontSizes.xs} showInParentheses />
          </Box>
        </Box>

        <Box>
          <Typography fontSize={theme.fontSizes.sm} fontWeight={theme.typography.fontWeightLight} color={theme.palette.text.secondary}>
            {t("Visitors today")}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
}

export default PinnedItemComponent;
